import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChefHat, Users, CalendarClock, Package as PackageIcon, Plus, Minus, Check, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/components/Seo";
import { GuestMinimumNotice } from "@/components/GuestMinimumNotice";

const EVENT_TYPES = ["wedding", "reception", "corporate", "birthday", "housewarming", "other"];

export default function BuildYourMenu() {
  useSeo({
    title: "Build Your Menu — Custom Catering Menu Builder | Tezz Khana Mumbai",
    description: "Build your own custom catering menu step by step for your event in Mumbai — choose your occasion, guest count, package, dishes and extras, then get a live price estimate.",
    path: "/build-your-menu",
  });
  const {
    t, eventInfo, setEventInfo, cart, addToCart, removeFromCart,
    extras, addExtra, decrementExtra,
    selectedPackageId, setSelectedPackageId,
  } = useApp();
  const navigate = useNavigate();
  const [step, setStep] = useState(eventInfo.type ? (eventInfo.guests ? 3 : 2) : 1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [c, d, p] = await Promise.all([
          api.get("/categories", { params: { include_unpublished: true } }),
          api.get("/dishes"),
          api.get("/packages"),
        ]);
        setCategories(c.data || []);
        setDishes(d.data || []);
        setPackages(p.data || []);
      } catch (e) { console.warn(e); }
    })();
  }, []);

  const menuCats = useMemo(() => categories.filter((c) => !c.is_extra && c.active !== false), [categories]);
  const extraCats = useMemo(() => categories.filter((c) => c.is_extra && c.active !== false), [categories]);
  const dishesByCat = useMemo(() => {
    const m = {};
    dishes.forEach((d) => { (m[d.category_id] = m[d.category_id] || []).push(d); });
    return m;
  }, [dishes]);

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === selectedPackageId) || null,
    [packages, selectedPackageId]
  );

  const steps = [
    { n: 1, label: "Event Type", icon: CalendarClock },
    { n: 2, label: "Guests", icon: Users },
    { n: 3, label: "Package", icon: PackageIcon },
    { n: 4, label: "Dishes", icon: ChefHat },
    { n: 5, label: "Extras", icon: Plus },
    { n: 6, label: "Review", icon: Check },
  ];

  const goCart = () => navigate("/cart");

  return (
    <div className="container-x py-16">
      <div className="max-w-2xl">
        <span className="kbd-eyebrow">Build Your Menu</span>
        <h1 className="mt-4 text-4xl font-semibold text-secondary sm:text-5xl">Build Your Menu</h1>
      </div>

      <ol className="mt-10 grid gap-2 md:grid-cols-6" data-testid="build-stepper">
        {steps.map((s) => {
          const Icon = s.icon;
          const active = step === s.n;
          const done = step > s.n;
          return (
            <li key={s.n} data-testid={`build-step-${s.n}`}
                className={`flex items-center gap-3 rounded-2xl border p-3 ${
                  active ? "border-primary bg-primary/5"
                  : done ? "border-accent/70 bg-accent/5" : "border-border/70 bg-card"
                }`}>
              <div className={`grid h-8 w-8 place-items-center rounded-full text-xs ${
                active ? "bg-primary text-primary-foreground"
                : done ? "bg-accent text-secondary" : "bg-muted text-secondary/70"
              }`}><Icon className="h-3.5 w-3.5" /></div>
              <div>
                <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Step {s.n}</div>
                <div className="text-sm font-medium text-secondary">{s.label}</div>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="mt-8 surface-card">
        {step === 1 && (
          <div data-testid="build-step-1-panel">
            <h2 className="font-serif-display text-2xl font-semibold text-secondary">What is the occasion?</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {EVENT_TYPES.map((et) => (
                <button key={et} data-testid={`build-event-${et}`}
                        onClick={() => setEventInfo({ ...eventInfo, type: et })}
                        className={`rounded-2xl border p-5 text-left ${eventInfo.type === et ? "border-primary bg-primary/5" : "border-border/70 bg-background"}`}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">Event</div>
                  <div className="mt-1 font-serif-display text-lg font-semibold text-secondary">{t(`event.types.${et}`)}</div>
                </button>
              ))}
            </div>
            <NavRow onNext={() => setStep(2)} disabled={!eventInfo.type} step={1} />
          </div>
        )}

        {step === 2 && (
          <div data-testid="build-step-2-panel">
            <h2 className="font-serif-display text-2xl font-semibold text-secondary">How many guests?</h2>
            <div className="mt-6 max-w-sm">
              <input type="number" min="1" data-testid="build-guest-count-input"
                value={eventInfo.guests} onChange={(e) => setEventInfo({ ...eventInfo, guests: e.target.value })}
                placeholder="e.g. 250"
                className="w-full rounded-xl border border-border/70 bg-background px-4 py-3 text-lg font-medium focus:border-primary focus:outline-none" />
              <p className="mt-3 text-xs text-muted-foreground">
                Guest count is used to compute your subtotal per the owner-approved package rate and per-head extras.
              </p>
              <GuestMinimumNotice
                active={Number(eventInfo.guests) >= 1 && Number(eventInfo.guests) < 50}
              />
            </div>
            <NavRow onBack={() => setStep(1)} onNext={() => setStep(3)}
                    disabled={!eventInfo.guests || Number(eventInfo.guests) < 1} step={2} />
          </div>
        )}

        {step === 3 && (
          <div data-testid="build-step-3-panel">
            <h2 className="font-serif-display text-2xl font-semibold text-secondary">Select a package</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {packages.map((p) => {
                const isSel = selectedPackageId === p.id;
                const priceKnown = p.price !== null && p.price !== undefined;
                const minKnown = p.minimum_guests !== null && p.minimum_guests !== undefined;
                return (
                  <button key={p.id} data-testid={`build-package-${p.tier}`}
                          onClick={() => setSelectedPackageId(p.id)}
                          className={`flex flex-col rounded-2xl border p-5 text-left transition-transform hover:-translate-y-0.5 ${
                            isSel ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border/70 bg-background"
                          }`}>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-accent">{p.tier}</div>
                    <div className="mt-1 font-serif-display text-xl font-semibold text-secondary">{p.name}</div>
                    {p.description && <p className="mt-2 text-sm text-muted-foreground">{p.description}</p>}
                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-muted px-2.5 py-1 font-semibold text-secondary/80">
                        Price: {priceKnown ? `₹${Number(p.price).toLocaleString("en-IN")}${p.pricing_unit ? ` / ${p.pricing_unit}` : ""}` : "TO BE CONFIGURED"}
                      </span>
                      <span className="rounded-full bg-muted px-2.5 py-1 font-semibold text-secondary/80">
                        Min guests: {minKnown ? p.minimum_guests : "TO BE CONFIGURED"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <GuestMinimumNotice
              active={!!selectedPackageId && Number(eventInfo.guests) >= 1 && Number(eventInfo.guests) < 50}
            />
            <NavRow onBack={() => setStep(2)} onNext={() => setStep(4)} disabled={!selectedPackageId} step={3} />
          </div>
        )}

        {step === 4 && (
          <div data-testid="build-step-4-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-serif-display text-2xl font-semibold text-secondary">Select your dishes</h2>
              <div className="text-xs text-muted-foreground">
                Package: <span className="font-medium text-secondary">{selectedPackage?.name || "—"}</span> ·
                Guests: <span className="font-medium text-secondary">{eventInfo.guests}</span>
              </div>
            </div>
            <div className="mt-3 rounded-xl bg-muted/60 p-3 text-xs text-muted-foreground">
              Selection limits (Select X of Y) per package are <strong>TO BE CONFIGURED</strong> by Admin.
              Until the owner finalises them, browse all published dishes below and add anything relevant for your event.
              Our team will confirm the final selection in the written quotation.
            </div>

            <div className="mt-6 space-y-8">
              {menuCats.map((cat) => {
                const ds = dishesByCat[cat.id] || [];
                if (ds.length === 0) return null;
                const rule = (selectedPackage?.category_rules || []).find((r) => r.category_id === cat.id);
                const limit = rule?.select_count ?? null;
                const selectedInCat = ds.filter((d) => cart.find((c) => c.id === d.id)).length;
                const atLimit = limit !== null && limit !== undefined && selectedInCat >= limit;
                const notInPackage = selectedPackage && (selectedPackage.category_rules || []).length > 0 && !rule;
                if (notInPackage) return null;
                return (
                  <div key={cat.id} data-testid={`build-menu-cat-${cat.slug}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif-display text-lg font-semibold text-secondary">{cat.name}</h3>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.2em]" data-testid={`build-menu-cat-count-${cat.slug}`}>
                        {limit !== null && limit !== undefined ? (
                          <span className={atLimit ? "text-accent" : "text-secondary"}>
                            {selectedInCat} of {limit} selected {atLimit ? "(limit reached)" : ""}
                          </span>
                        ) : <span className="text-muted-foreground">{selectedInCat} selected</span>}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {ds.map((d) => {
                        const inCart = !!cart.find((c) => c.id === d.id);
                        const disabled = !inCart && atLimit;
                        return (
                          <button key={d.id} data-testid={`build-dish-${d.id}`} disabled={disabled}
                                  onClick={() => inCart ? removeFromCart(d.id) : addToCart(d)}
                                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left text-sm ${
                                    inCart ? "border-primary bg-primary/5"
                                    : disabled ? "border-border/40 bg-muted/40 opacity-50 cursor-not-allowed"
                                    : "border-border/70 bg-background hover:bg-muted"
                                  }`}>
                            <span className="truncate">
                              <span className="font-medium text-secondary">{d.name}</span>
                              {d.seasonal && <span className="ml-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-accent">Seasonal</span>}
                            </span>
                            {inCart ? <Check className="h-4 w-4 shrink-0 text-primary" /> : <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <NavRow onBack={() => setStep(3)} onNext={() => setStep(5)} disabled={cart.length === 0} step={4} />
          </div>
        )}

        {step === 5 && (
          <div data-testid="build-step-5-panel">
            <h2 className="font-serif-display text-2xl font-semibold text-secondary">Optional extras (per head)</h2>
            <div className="mt-3 rounded-xl bg-muted/60 p-3 text-xs text-muted-foreground">
              Extras are charged per guest at the owner-approved category rate. Any rate marked
              <span className="mx-1 rounded-md bg-background px-1.5 py-0.5 font-semibold text-secondary">TO BE CONFIGURED</span>
              will not be included in the subtotal until Admin sets it.
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {extraCats.map((cat) => {
                const rateKnown = cat.extra_rate_per_head !== null && cat.extra_rate_per_head !== undefined;
                const rateStr = rateKnown
                  ? `₹${Number(cat.extra_rate_per_head).toLocaleString("en-IN")} / head`
                  : "TO BE CONFIGURED";
                // A category-level extra selection uses a synthetic id "cat:<category_id>" so it slots into the extras list.
                const catExtraId = `cat:${cat.id}`;
                const item = extras.find((e) => e.dish_id === catExtraId);
                const qty = item?.qty || 0;
                return (
                  <div key={cat.id} data-testid={`build-extra-cat-${cat.slug}`}
                       className="flex items-center justify-between gap-3 rounded-2xl border border-border/70 bg-background p-4">
                    <div className="min-w-0">
                      <div className="font-serif-display text-base font-semibold text-secondary">{cat.name}</div>
                      <div className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                        rateKnown ? "bg-accent/20 text-secondary" : "bg-muted text-muted-foreground"
                      }`} data-testid={`extra-cat-rate-${cat.slug}`}>{rateStr}</div>
                    </div>
                    {qty > 0 ? (
                      <div className="inline-flex items-center gap-1 rounded-full border border-border/70 p-0.5" data-testid={`extra-cat-qty-control-${cat.slug}`}>
                        <button data-testid={`extra-cat-decrement-${cat.slug}`}
                                onClick={() => decrementExtra(catExtraId)}
                                className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted" aria-label="Decrease">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-6 text-center text-sm font-semibold" data-testid={`extra-cat-qty-${cat.slug}`}>{qty}</span>
                        <button data-testid={`extra-cat-increment-${cat.slug}`}
                                onClick={() => addExtra({ id: catExtraId, name: cat.name }, cat)}
                                className="grid h-8 w-8 place-items-center rounded-full text-primary hover:bg-primary/10" aria-label="Increase">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button data-testid={`extra-cat-add-${cat.slug}`}
                              onClick={() => addExtra({ id: catExtraId, name: cat.name }, cat)}
                              className="inline-flex items-center gap-1 rounded-full border border-border/70 px-4 py-1.5 text-xs font-semibold text-secondary hover:bg-muted">
                        <Plus className="h-3.5 w-3.5" /> Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <NavRow onBack={() => setStep(4)} onNext={() => setStep(6)} step={5} />
          </div>
        )}

        {step === 6 && (
          <div data-testid="build-step-6-panel">
            <h2 className="font-serif-display text-2xl font-semibold text-secondary">Review</h2>
            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-4">
              <Meta label="Event">{eventInfo.type ? t(`event.types.${eventInfo.type}`) : "—"}</Meta>
              <Meta label="Guests">{eventInfo.guests || "—"}</Meta>
              <Meta label="Package">{selectedPackage?.name || "—"}</Meta>
              <Meta label="Dishes">{cart.length} + {extras.length} extras</Meta>
            </div>
            <div className="mt-6 flex flex-col justify-between gap-3 sm:flex-row">
              <button data-testid="build-step-6-back" onClick={() => setStep(5)} className="btn-ghost">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button data-testid="build-step-6-go-cart" onClick={goCart} className="btn-primary">
                Go to Cart & Price Summary <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({ label, children }) {
  return (
    <div className="rounded-xl border border-border/70 bg-background p-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium text-secondary">{children}</div>
    </div>
  );
}

function NavRow({ onBack, onNext, disabled, step }) {
  return (
    <div className="mt-8 flex justify-between">
      {onBack ? (
        <button data-testid={`build-step-${step}-back`} onClick={onBack} className="btn-ghost">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
      ) : <span />}
      {onNext && (
        <button data-testid={`build-step-${step}-next`} onClick={onNext} disabled={disabled} className="btn-primary">
          Next <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
